import React, { lazy, Suspense } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { apm } from '@/lib/apm';
import ContextualPanelSkeleton from '@/components/Placeholders/ContextualPanelSkeleton';

function PreviewFailure({ onRetry, onClose }) {
  const { colors } = useTheme();
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text accessibilityRole="header" style={[styles.title, { color: colors.text_color_primary }]}>
        Couldn’t open this preview
      </Text>
      <Text style={[styles.message, { color: colors.text_color_secondary }]}>
        Try again, or close the preview to return to your chat.
      </Text>
      <View style={styles.actions}>
        <Pressable accessibilityRole="button" accessibilityLabel="Retry preview" onPress={onRetry}
          style={[styles.button, { backgroundColor: colors.solid_color_secondary }]}>
          <Text style={{ color: colors.text_color_primary }}>Try again</Text>
        </Pressable>
        <Pressable accessibilityRole="button" accessibilityLabel="Close preview" onPress={onClose}
          style={[styles.button, { backgroundColor: colors.solid_color_secondary }]}>
          <Text style={{ color: colors.text_color_primary }}>Close preview</Text>
        </Pressable>
      </View>
    </ScrollView>
  );
}

function sourceIdentity(props) {
  const file = props.file;
  return {
    file: file?.id || file?._id || file?.cdnPath || file?.url || file?.uri || file,
    task: props.taskId,
    conversation: props.conversationId,
  };
}

export default function createLazyArtifactPanel(load) {
  return class LazyArtifactPanel extends React.Component {
    constructor(props) {
      super(props);
      this.state = { Panel: lazy(load), failed: false, source: sourceIdentity(props) };
    }

    static getDerivedStateFromError() { return { failed: true }; }

    static getDerivedStateFromProps(props, state) {
      const source = sourceIdentity(props);
      if (source.file === state.source.file && source.task === state.source.task
        && source.conversation === state.source.conversation) return null;
      // New artifacts can recover a failed panel. Healthy editors retain their
      // component identity and local state through ordinary prop updates.
      return { source, ...(state.failed ? { failed: false, Panel: lazy(load) } : {}) };
    }

    componentDidCatch(error, info) {
      try { apm.captureError(error, { componentStack: info?.componentStack?.slice(0, 4000) || '' }); }
      catch (_) { /* Reporting must not break recovery. */ }
    }

    retry = () => {
      // React.lazy remembers a rejection; remounting that same type cannot
      // retry its loader. A fresh type makes an explicit new load attempt.
      this.setState({ failed: false, Panel: lazy(load) });
    };

    render() {
      if (this.state.failed) return <PreviewFailure onRetry={this.retry} onClose={this.props.onClose} />;
      const { Panel } = this.state;
      // Keep Suspense inside this boundary so its lazy type survives the
      // initial suspension instead of being recreated on every render.
      return <Suspense fallback={<ContextualPanelSkeleton />}><Panel {...this.props} /></Suspense>;
    }
  };
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { flexGrow: 1, justifyContent: 'center', padding: 24, gap: 14 },
  title: { fontSize: 18, fontWeight: '600' },
  message: { fontSize: 14, lineHeight: 21 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  button: { minHeight: 40, justifyContent: 'center', paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8 },
});
